/* eslint-disable max-lines */
import { ArrowheadDown, ArrowheadRight } from '@elliemae/ds-icons';
import { SimpleTruncatedTooltipText } from '@elliemae/ds-truncated-tooltip-text';
import React, { useCallback, useEffect, useMemo } from 'react';
import { ITEM_TYPES, LEFT_NAVIGATION_DATA_TESTID } from '../../constants/index.js';
import { ItemRenderer } from '../../exported-related/index.js';
import type { DSLeftNavigationT } from '../../react-desc-prop-types.js';
import { ItemSeparator } from '../ItemSeparator/index.js';
import { ItemTextLabel } from '../ItemTextLabel/index.js';
import { ItemWithDate } from '../ItemWithDate/index.js';
import { StyledArrowContainer, StyledDate, StyledLabel, StyledRightContainer } from '../styled.js';

// Subitem mapping
const outOfTheBoxSubitemsComponents: Record<string, React.ComponentType<DSLeftNavigationT.ComponentProps>> = {
  [ITEM_TYPES.LEFT_NAV_ITEM_SEPARATOR]: ItemSeparator,
  [ITEM_TYPES.LEFT_NAV_ITEM_WITH_DATE]: ItemWithDate,
  [ITEM_TYPES.LEFT_NAV_ITEM_TEXT_LABEL]: ItemTextLabel,
};

const OutOfTheBoxMapSubitem: React.ComponentType<DSLeftNavigationT.ComponentProps> = (props) => {
  const {
    item,
    item: { type, Component },
    ctx,
  } = props;

  if (typeof type === 'string' && !!outOfTheBoxSubitemsComponents[type]) {
    const OutOfTheBoxComponent = outOfTheBoxSubitemsComponents[type];
    const currentIndent = item.itemOpts?.indent ?? 0;
    const newItem = {
      ...item,
      itemOpts: { ...item.itemOpts, indent: currentIndent + 44 },
    };
    return <OutOfTheBoxComponent item={newItem} ctx={ctx} />;
  }
  if (!Component) return null;
  // we expect an error to be threw if Component is not defined and type is not a valid out-of-the-box...
  return <Component item={item} ctx={ctx} />;
};

export const ItemUncontrolledDrilldown = (props: DSLeftNavigationT.ComponentProps): JSX.Element => {
  const {
    item,
    item: {
      dsId,
      itemOpts: {
        label,
        RightComponent,
        labelRightSection,
        labelOverflow: itemLabelOverflow,
        labelBold,
        labelColor,
        startOpened,
        items,
        labelDataTestId,
      },
    },
    ctx,
    ctx: {
      leftNavProps,
      leftNavProps: { labelOverflow: leftNavLabelOverflow },
      openedDrilldowns,
      setOpenedDrilldowns,
    },
  } = props;
  const getOwnerProps = React.useCallback(() => leftNavProps, [leftNavProps]);
  const getOwnerPropsArguments = React.useCallback(() => ({}), []);

  // Start opened functionality
  useEffect(() => {
    if (startOpened) setOpenedDrilldowns([...openedDrilldowns, dsId as string]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opened = openedDrilldowns.includes(dsId as string);

  const labelOverflow = itemLabelOverflow || leftNavLabelOverflow;

  const arrowIcon = useMemo(() => {
    const Component = opened ? ArrowheadDown : ArrowheadRight;
    return <Component width="24px" height="18px" color={['brand-primary', '800']} />;
  }, [opened]);

  // Array of subitems
  const subitems = useMemo(() => {
    if (!opened || !items) return [];
    return items.map((subitem) => <OutOfTheBoxMapSubitem item={subitem} ctx={ctx} key={subitem.dsId} />);
  }, [ctx, items, opened]);

  const handleOnClick = useCallback(() => {
    if (opened) setOpenedDrilldowns(openedDrilldowns.filter((openedDrilldown) => openedDrilldown !== dsId));
    else setOpenedDrilldowns([...openedDrilldowns, dsId as string]);
  }, [opened, setOpenedDrilldowns, openedDrilldowns, dsId]);

  return (
    <ItemRenderer
      item={{
        ...item,
        itemOpts: {
          ariaExpanded: opened,
          ariaRole: 'menuitem',
          ...item.itemOpts,
          openable: !opened,
          closable: opened,
        },
        CollapsedComponent: null,
      }}
      subitems={subitems}
      minHeight="32px"
      paddingTop="8px"
      onClick={handleOnClick}
    >
      <StyledArrowContainer
        pl="xxs"
        mt="-2px"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.ARROW_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {arrowIcon}
      </StyledArrowContainer>
      <StyledLabel
        pb="xxxs"
        labelOverflow={labelOverflow}
        labelBold={labelBold}
        labelColor={labelColor}
        data-testid={labelDataTestId || LEFT_NAVIGATION_DATA_TESTID.LIST_ITEM_LABEL_ID_EXCEPTION}
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {labelOverflow === 'truncate' ? <SimpleTruncatedTooltipText value={label} placement="bottom-start" /> : label}
      </StyledLabel>
      <StyledRightContainer
        pr="xxs2"
        cols={['auto', 'auto']}
        alignItems="center"
        gutter="xxs"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.RIGHT_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        <StyledDate
          ml="xxs"
          data-testid={LEFT_NAVIGATION_DATA_TESTID.ITEM_DATE} // https://jira.elliemae.io/browse/PUI-15589
          getOwnerProps={getOwnerProps}
          getOwnerPropsArguments={getOwnerPropsArguments}
        >
          {labelRightSection}
        </StyledDate>
        {RightComponent && <RightComponent ctx={ctx} item={item} />}
      </StyledRightContainer>
    </ItemRenderer>
  );
};
